<template>
	<row-object-dialog v-model="data" :oref="oref" :name="name" :buttons="buttons" :force-info="forceInfo"
					   :display="{ mode: 'form'}"
					   :parent-errors="parentErrors" :subviews="subviews" :primary="primary"
					   :build-url="buildUrl"
					   :build-badge-url="buildBadgeUrl"
					   :build-endpoint="buildEndpoint"
					   :add-save="addSave"
					   :add-save-and-back="addSaveAndBack"
					   ref="rowObjectDialog"><template #hero><slot name="hero"></slot></template></row-object-dialog>
</template>

<script setup lang="ts">
/*
	RowObjectPage adds capability to control sidebar menu content and stack path
	to RowObjectDialog (which handles persistance, i.e. load/save operations.
 */
import {ref, onUnmounted, ComputedRef, computed} from "vue";
import {SubviewTab} from "@t/ui/SubView.ts";
import {RowObjectRef, SourceType} from "@t/meta/SourceType.ts";
import {AppContextMap} from "@t/fd/AppContext.ts";
import type {ErrorMap} from "@fd/PartInjectKeys.ts";
import type {FormPartOrReference} from "@t/fd/FormPartOrReference.ts";
import {useNavigationStore} from "@/store/NavigationStore.ts";
import RowObjectDialog from "@/pages/row-object/RowObjectDialog.vue";
import type {FormData} from "@t/fd/FormPart.ts";


const props = withDefaults(defineProps<{
	/** Reference to an object to be displayed */
	oref: RowObjectRef
	/** Name used to save subview layout*/
	name?: string;

	/** List of buttons supported by the parent component */
	buttons?: FormPartOrReference[];
	/** Prevent adding standard Save button, defaults to true */
	addSave?: boolean;
	/** Prevent adding standard SaveAndBack button, defaults to true */
	addSaveAndBack?: boolean;

	/** Display force info component */
	forceInfo?: boolean;

	/** Display subviews around the form (if available) */
	subviews?: boolean;

	/**
	 * This is the primary dialog of the page
	 * App context of the primary dialog is used to build sidebar menu. Defaults to "true"
	 * Make sure to define the `name` property
	 **/
	primary?: boolean;

	/** Can be used to alter subview URL (if not component based) */
	buildUrl?: (tab:SubviewTab) => string;
	/** Parent component can add extra params to /api/subviews/badges endpoint call */
	buildBadgeUrl?: (endpoint:string) => string;

	/* handlers to "hack" behavior */
	buildEndpoint?: (endpoint:string, edit:boolean, type:SourceType) => string;

	/** Extra errors emited by the parent */
	parentErrors?: ErrorMap;
}>(), {
	buttons: () => [],
	addSave: true,
	addSaveAndBack: true,
	forceInfo: false,
	subviews: true,
	primary: true,
	buildEndpoint: (endpoint:string, e:boolean, t:SourceType) => endpoint,
	parentErrors: () => []
});

const data = defineModel<FormData|null>({ required:true });

const ns = useNavigationStore();
const rowObjectDialog = ref<InstanceType<typeof RowObjectDialog> | null>(null);

const pagePrimaryContext = computed<AppContextMap>(() => {
	if (rowObjectDialog.value) {
		const dc = rowObjectDialog.value.getDialogContext();
		return dc?.value ?? {};
	} else return {};
});

const pagePrimaryRowObject = computed<RowObjectRef>(() => props.oref);

defineExpose({
	getDialogContext: ():ComputedRef<AppContextMap>|null => {
		return pagePrimaryContext;
	},
	okLoaded: () => {
		return rowObjectDialog.value != null && rowObjectDialog.value.okLoaded();
	}
});

if (props.primary) {
	if (!props.name) console.error("Primary page doesn't have a name");
	else ns.definePage(props.name, pagePrimaryRowObject, pagePrimaryContext);
}

onUnmounted(() => {
	ns.undisplay();
});
</script>

<style>
</style>
